import React from 'react';
import { useTranslation } from 'react-i18next';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  type = 'warning'
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const getTypeColors = () => {
    switch (type) {
      case 'danger':
        return {
          icon: 'text-red-400',
          confirmButton: 'bg-gradient-to-r from-red-500 to-red-600 hover:shadow-lg'
        };
      case 'warning':
        return {
          icon: 'text-yellow-400',
          confirmButton: 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:shadow-lg'
        };
      case 'info':
        return {
          icon: 'text-blue-400',
          confirmButton: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-lg'
        };
      default:
        return {
          icon: 'text-yellow-400',
          confirmButton: 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:shadow-lg'
        };
    }
  };

  const colors = getTypeColors();

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-6">
          {/* Icon ve Başlık */}
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg className={`h-8 w-8 ${colors.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {type === 'danger' && (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                )}
                {type === 'warning' && (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                )}
                {type === 'info' && (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                )}
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-white">{title}</h3>
            </div>
          </div>

          {/* Mesaj */}
          <div className="mb-6">
            <p className="text-white/80 leading-relaxed">{message}</p>
          </div>

          {/* Butonlar */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-all duration-200"
            >
              {cancelText || t('common.cancel')}
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 ${colors.confirmButton} text-white rounded-lg font-medium transition-all duration-200 transform hover:scale-105`}
            >
              {confirmText || t('common.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal; 